import { useState, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Trophy, Crown, Star, TrendingUp, Zap } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { userApi } from '@/utils/api';

const LeaderboardItem = memo(({ user, index, onClick }: { user: any; index: number; onClick: () => void }) => {
    const isTop3 = index < 3;
    const rankColors = ['text-amber-400', 'text-slate-300', 'text-amber-700'];

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.03 }}
            onClick={onClick}
            className="group relative flex items-center gap-4 glass backdrop-blur-xl border border-white/[0.05] p-3 rounded-2xl active:scale-95 transition-all mb-2 hover:bg-white/[0.08] cursor-pointer"
        >
            {/* Rank Number/Icon */}
            <div className="flex flex-col items-center justify-center w-8">
                {isTop3 ? (
                    <Trophy className={rankColors[index]} size={18} fill="currentColor" />
                ) : (
                    <span className="text-xs font-black text-white/30">#{index + 1}</span>
                )}
            </div>

            {/* Avatar Profile */}
            <div className="relative">
                <div className="w-11 h-11 rounded-full border-2 border-[#0A0E17] bg-tg-secondary overflow-hidden shadow-lg group-hover:border-white/20 transition-colors">
                    <img
                        src={user.photo_url || '/icons/default-avatar.svg'}
                        alt={user.username}
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-amber-500 text-black text-[8px] font-black px-1 rounded-md border border-[#0A0E17]">
                    Lvl {user.level}
                </div>
            </div>

            {/* User Info */}
            <div className="flex-1">
                <h4 className="text-sm font-black text-white tracking-tight truncate max-w-[120px]">
                    {user.first_name}
                </h4>
                <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider">
                    @{user.username || 'user'}
                </p>
            </div>

            {/* Score */}
            <div className="text-right flex flex-col items-end">
                <div className="flex items-center gap-1.5 text-blue-400">
                    <TrendingUp size={10} strokeWidth={3} />
                    <span className="text-xs font-black tracking-tight">{Math.floor(user.total_xp).toLocaleString()}</span>
                </div>
                <span className="text-[8px] text-white/20 font-black uppercase tracking-widest mt-0.5">Elite Score</span>
            </div>

            {/* Hover Shine Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none" />
        </motion.div>
    );
});

const Leaderboard = ({ onBack, onNavigate }: { onBack: () => void; onNavigate: (page: string, data?: any) => void }) => {
    const { impactOccurred } = useHapticFeedback();
    const [leaderboard, setLeaderboard] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                const res = await userApi.getLeaderboard();
                const data = await res.json();
                if (data.success) {
                    setLeaderboard(data.leaderboard);
                }
            } catch (e) {
                console.error("Failed to fetch leaderboard", e);
            } finally {
                setIsLoading(false);
            }
        };
        fetchLeaderboard();
    }, []);

    const top3 = leaderboard.slice(0, 3);
    const rest = leaderboard.slice(3);

    return (
        <div className="min-h-screen bg-tg-bg text-white pb-32">
            {/* Header Area */}
            {/* Header Area */}
            <div className="relative pt-8 pb-12 px-6 overflow-hidden">
                {/* Background Glows */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-screen h-[500px] bg-gradient-to-b from-blue-600/20 via-purple-900/10 to-transparent blur-3xl opacity-60 pointer-events-none" />

                <div className="relative z-10">
                    {/* Top Bar */}
                    <div className="flex items-center justify-between mb-8">
                        <button
                            onClick={() => { impactOccurred('light'); onBack(); }}
                            className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-md flex items-center justify-center border border-white/10 active:scale-90 transition-transform"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500">
                            <Crown size={20} fill="currentColor" />
                        </div>
                    </div>

                    {/* Main Title */}
                    <div className="text-center">
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            className="inline-block"
                        >
                            <h1 className="text-4xl font-black italic uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/50 drop-shadow-2xl">
                                Hall of Fame
                            </h1>
                        </motion.div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.4em] mt-2"
                        >
                            The Utlimate 50
                        </motion.p>
                    </div>
                </div>
            </div>

            {/* Podium Section (Top 3) */}
            <div className="px-6 mb-8 pt-4">
                <div className="grid grid-cols-2 gap-3">
                    {/* Rank 1 Feature Tile */}
                    {top3[0] && (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            onClick={() => { impactOccurred('medium'); onNavigate('public_profile', top3[0]); }}
                            className="col-span-2 relative bg-gradient-to-br from-amber-500/20 to-tg-secondary border-2 border-amber-500/30 rounded-[32px] p-6 flex flex-col items-center justify-center overflow-hidden active:scale-95 transition-all group cursor-pointer"
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-30">
                                <Crown size={64} className="text-amber-500 rotate-12" />
                            </div>
                            <div className="relative z-10">
                                <div className="absolute -top-2 -right-2 bg-amber-500 text-black w-7 h-7 rounded-full flex items-center justify-center border-2 border-[#0A0E17] shadow-lg">
                                    <Crown size={14} fill="currentColor" />
                                </div>
                                <div className="w-24 h-24 rounded-full border-4 border-amber-500 p-1 mb-4 shadow-[0_0_30px_rgba(245,158,11,0.3)]">
                                    <img
                                        src={top3[0].photo_url || '/icons/default-avatar.svg'}
                                        className="w-full h-full rounded-full object-cover"
                                        alt="Apex"
                                    />
                                </div>
                            </div>
                            <div className="text-center relative z-10">
                                <h3 className="text-xl font-black text-white italic drop-shadow-md">{top3[0].first_name}</h3>
                                <p className="text-amber-400 text-[10px] font-black uppercase tracking-widest mb-2">Global Apex Legend</p>
                                <div className="inline-flex items-center gap-2 bg-black/40 px-3 py-1 rounded-full border border-white/10">
                                    <Star size={10} className="text-amber-400 fill-amber-400" />
                                    <span className="text-xs font-black">{Math.floor(top3[0].total_xp).toLocaleString()} XP</span>
                                </div>
                            </div>
                            {/* Particle/Glow backdrops */}
                            <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-amber-500/10 to-transparent" />
                        </motion.div>
                    )}

                    {/* Ranks 2 & 3 */}
                    <div className="grid grid-cols-2 gap-3 col-span-2">
                        {top3.slice(1).map((user, i) => (
                            <motion.div
                                key={user.telegram_id}
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.2 + i * 0.1 }}
                                onClick={() => { impactOccurred('medium'); onNavigate('public_profile', user); }}
                                className="glass backdrop-blur-xl border border-white/10 rounded-[28px] p-4 flex flex-col items-center justify-center relative overflow-hidden active:scale-95 transition-all group cursor-pointer"
                            >
                                <div className="w-16 h-16 rounded-full border-2 border-white/20 p-1 mb-2 relative z-10">
                                    <img src={user.photo_url || '/icons/default-avatar.svg'} className="w-full h-full rounded-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" alt="silver" />
                                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center border-2 border-[#0A0E17] shadow-lg ${i === 0 ? 'bg-slate-300' : 'bg-amber-700'}`}>
                                        <Trophy size={12} className="text-black/80" fill="currentColor" />
                                    </div>
                                </div>
                                <h4 className="text-sm font-black text-white italic truncate max-w-[90px]">{user.first_name}</h4>
                                <span className={`text-[9px] font-black uppercase ${i === 0 ? 'text-slate-300' : 'text-amber-700'}`}>
                                    Rank #{i + 2}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Full List Section */}
            <div className="px-6">
                <div className="flex items-center justify-between mb-4 px-2">
                    <h2 className="text-[10px] font-black text-white/40 uppercase tracking-widest">Global Top 50</h2>
                    <div className="flex items-center gap-1.5 text-[10px] text-blue-400 font-bold">
                        <Zap size={10} fill="currentColor" />
                        <span>Elite Scoring Active</span>
                    </div>
                </div>

                <div className="space-y-1">
                    {isLoading ? (
                        [1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="h-16 w-full bg-white/5 animate-pulse rounded-2xl mb-2" />
                        ))
                    ) : (
                        rest.map((user, i) => (
                            <LeaderboardItem
                                key={user.telegram_id}
                                user={user}
                                index={i + 3}
                                onClick={() => { impactOccurred('light'); onNavigate('public_profile', user); }}
                            />
                        ))
                    )}
                </div>

                {/* Footer Info */}
                <div className="mt-8 text-center px-4">
                    <p className="text-[9px] text-white/20 font-bold uppercase tracking-[0.2em] leading-relaxed">
                        Influences is calculated via your Total Spent, Total Earned, and Bonus XP gathered from platform engagement.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Leaderboard;
